/**
 * useOnboardingMutations
 *
 * Persistence strategy (four atomic steps):
 *
 * A) complete_onboarding RPC — handles the heavy lift:
 *    profiles:      first_name, last_name, birthday, avatar_url, is_onboarded = true
 *    user_settings: goal_value, year_type, fiscal_start_month,
 *                   ai_suggestions_enabled, push_reminders, email_reminders
 *
 * B) Direct upsert on user_settings — columns not covered by the RPC:
 *    goal_type, goal_period.
 *
 * C) Direct UPDATE on profiles — birthday integrity (MM-DD) + the
 *    three-pillar notification preferences:
 *      - reminder_enabled  (Smart Reminder pillar — daily alarm)
 *      - digest_enabled    (Strategic Digest pillar)
 *    Frequency/time default on the DB column definitions.
 *
 * D) Fire-and-forget welcome email.
 *
 * After all four succeed, refreshUserData() re-hydrates the AppContext so
 * every downstream consumer (Analytics, Dashboard, Settings) sees fresh data.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useApp } from "@/contexts/AppContext";
import { trackOnboardingCompleted } from "@/lib/analytics";
import { resolveOAuthProfileBasicsFromUser } from "@/lib/profileAvatar";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export interface OnboardingPayload {
  firstName: string;
  lastName: string;
  birthday: string | null;  // MM-DD, null until confirmed
  avatarUrl: string | null;
  officeGoal: number;         // 0-100 (percent) or 1-5 (days)
  officeGoalType: "days" | "percent";
  officeGoalPeriod: "week" | "month" | "quarter";
  yearType: "natural" | "fiscal";
  fiscalStartMonth: number;         // 1-12
  aiEnabled: boolean;
  pushEnabled: boolean;
  emailEnabled: boolean;
}

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────

export function useOnboardingMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { refreshUserData } = useApp();
  const intl = useIntl();

  // ── Complete onboarding ────────────────────────────────────────────────
  const completeOnboarding = useMutation({
    mutationFn: async (payload: OnboardingPayload) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error("No active session found.");
      const userId = user.id;

      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("id", userId)
        .maybeSingle();

      const oauthProfile = resolveOAuthProfileBasicsFromUser(user);
      const avatarUrl =
        payload.avatarUrl ??
        oauthProfile?.avatarUrl ??
        existingProfile?.avatar_url ??
        null;

      // ── A: RPC — bulk write (profiles + user_settings core fields) ──────
      const { error: rpcError } = await supabase.rpc("complete_onboarding", {
        p_first_name: payload.firstName,
        p_last_name: payload.lastName,
        p_avatar_url: avatarUrl,
        p_goal_value: payload.officeGoal,
        p_year_type: payload.yearType,
        p_fiscal_start_month: payload.fiscalStartMonth,
        p_ai_enabled: payload.aiEnabled,
        p_push_enabled: payload.pushEnabled,
        p_email_enabled: payload.emailEnabled,
      });
      if (rpcError) throw rpcError;

      // ── B: Direct upsert — goal_type + goal_period (not in RPC) ──────────
      const { error: settingsError } = await supabase
        .from("user_settings")
        .upsert({
          user_id: userId,
          goal_type: payload.officeGoalType,
          goal_period: payload.officeGoalPeriod,
        });
      if (settingsError) throw settingsError;

      // ── C: Direct update on profiles — birthday integrity + three-pillar
      //       notification preferences. `reminder_enabled` governs the
      //       Smart Reminder daily alarm; `digest_enabled` governs the
      //       Strategic Digest. Frequency/time use DB defaults.
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          birthday:           payload.birthday,
          birthday_confirmed: Boolean(payload.birthday),
          birthday_source:    payload.birthday ? "manual" : null,
          reminder_enabled:   payload.pushEnabled,
          digest_enabled:     payload.emailEnabled,
        })
        .eq("id", userId);
      if (profileError) throw profileError;

      // ── D: Fire-and-forget Welcome Email ────────────────────────────────
      try {
        const email = user.email;
        if (email) {
          supabase.functions.invoke('send-welcome', {
            body: { 
              email, 
              user_id: userId,
              first_name: payload.firstName 
            }
          }).catch(err => console.error("Email trigger failed silently:", err));
        }
      } catch (e) {
        console.error("Welcome email logic error:", e);
      }
    },

    onSuccess: async () => {
      trackOnboardingCompleted();
      // Re-hydrate AppContext → profile.isOnboarded becomes true → wizard unmounts
      await refreshUserData();

      // Invalidate any TanStack Query caches for belt-and-suspenders sync
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["settings"] });

      toast({
        title: intl.formatMessage({ id: "onboarding.toast.welcome.title" }),
        description: intl.formatMessage({ id: "onboarding.toast.welcome.description" }),
      });
    },

    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "onboarding.toast.failed.title" }),
        description:
          error.message ||
          intl.formatMessage({ id: "onboarding.toast.failed.description" }),
      });
    },
  });

  // ── Avatar upload ──────────────────────────────────────────────────────
  const uploadAvatar = useMutation({
    mutationFn: async ({ file, userId }: { file: File; userId: string }) => {
      try {
        const ext = file.name.split(".").pop() ?? "jpg";
        const fileName = `${userId}/${Date.now()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, file, { upsert: true });

        if (uploadError) {
          console.error("Supabase storage upload error:", uploadError);
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from("avatars")
          .getPublicUrl(fileName);

        if (!publicUrl) {
          throw new Error("Failed to retrieve public URL for uploaded avatar.");
        }

        return publicUrl;
      } catch (error) {
        console.error("Avatar upload process failed:", error);
        throw error;
      }
    },
  });

  return { completeOnboarding, uploadAvatar };
}
